import { oids } from "@/configs/Settings";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import {
  isAnyFluxmedDefaultMessageTemplate,
  isMessageTemplateListedForSend,
} from "src-next/modules/crm-clinical/messageTemplates/utils/messageTemplateUiUtils";
import type { MessageTemplate } from "src-next/modules/crm-clinical/messageTemplates/types";
import type {
  MessagingAppointment,
  MessagingPatient,
  MessagingQuestionnaireOption,
  MessagingTemplate,
} from "src-next/modules/crm-clinical/messaging/types";
import { normalizeCpf } from "src-next/shared/utils/cpf";

import type { AutomationContext } from "../types";
import { resolveQuestionnaireIdFromTemplate } from "./automationTemplateUtils";

const LOG_PREFIX = "[automationTrigger]";

export type AutomationMessagingTriggerId =
  | "triage"
  | "preConsultation"
  | "postConsultation"
  | "birthday";

export type AutomationTriggerSkipResult = {
  status: "skipped";
  reason: string;
};

/**
 * Extrai o identificador numérico/alphanum da referência FHIR (`ResourceType/id`).
 */
export function extractResourceIdFromReference(
  reference?: string | null,
): string {
  const trimmed = String(reference ?? "").trim();
  if (!trimmed) return "";
  const parts = trimmed.split("/");
  return parts[parts.length - 1]?.trim() ?? "";
}

/**
 * Tenta extrair CPF (11 dígitos) de referências `Patient/{oid}-{cpf}` ou strings similares.
 */
export function extractCpfFromPatientReference(
  reference?: string | null,
): string {
  const trimmed = String(reference ?? "").trim();
  if (!trimmed) return "";

  const afterSlash = trimmed.includes("/")
    ? trimmed.split("/").pop() ?? trimmed
    : trimmed;

  const cpfCandidate = afterSlash.includes("-")
    ? afterSlash.split("-").pop() ?? afterSlash
    : afterSlash;

  return normalizeCpf(cpfCandidate);
}

/**
 * Normaliza organizationId para o contrato do módulo de automação (CNPJ sem prefixo OID).
 */
export function normalizeAutomationOrganizationId(
  ...candidates: (string | undefined | null)[]
): string {
  return resolveCrmOrganizationId(...candidates) ?? "";
}

/**
 * Monta AutomationContext a partir de ids já resolvidos pelo caller legado.
 */
export function buildAutomationContext(params: {
  organizationId: string;
  practitionerId: string;
  whatsappBusinessAccountId?: string;
}): AutomationContext | null {
  const organizationId = normalizeAutomationOrganizationId(
    params.organizationId,
  );
  const practitionerId = String(params.practitionerId ?? "").trim();

  if (!organizationId || !practitionerId) {
    return null;
  }

  return {
    organizationId,
    practitionerId,
    whatsappBusinessAccountId: params.whatsappBusinessAccountId,
  };
}

/**
 * Converte MessageTemplate (domínio CRM) para MessagingTemplate (domínio de envio).
 */
export function mapMessageTemplateToMessagingTemplate(
  template: MessageTemplate,
): MessagingTemplate {
  return {
    id: template.id,
    name: template.name,
    preview: template.preview,
    header: template.header || undefined,
    status: template.status,
  };
}

/**
 * Filtra template configurado na automação e valida elegibilidade para envio.
 */
export function findAutomationMessageTemplateForSend(params: {
  templates: MessageTemplate[];
  messageTemplateId: string;
  organizationId: string;
}): MessageTemplate | null {
  const messageTemplateId = String(params.messageTemplateId ?? "").trim();
  if (!messageTemplateId) return null;

  const organizationId = normalizeAutomationOrganizationId(
    params.organizationId,
  );

  const template = params.templates.find(
    (item) => String(item.id ?? "").trim() === messageTemplateId,
  );
  if (!template) return null;

  if (isAnyFluxmedDefaultMessageTemplate(template.name, organizationId)) {
    return null;
  }

  if (!isMessageTemplateListedForSend(template.status)) {
    return null;
  }

  return template;
}

/**
 * Monta opção de questionário quando o template exige `{{questionnaire_link}}`.
 */
export function buildQuestionnaireOptionFromTemplate(params: {
  template: MessageTemplate;
  questionnaireNameById: Map<string, string>;
}): MessagingQuestionnaireOption | null {
  const questionnaireId = resolveQuestionnaireIdFromTemplate(params.template);
  if (!questionnaireId) return null;

  return {
    id: questionnaireId,
    title:
      params.questionnaireNameById.get(questionnaireId) ??
      "Questionário vinculado",
  };
}

export function buildMessagingAppointment(params: {
  appointmentId: string | number;
  startAtIso?: string | null;
}): MessagingAppointment {
  return {
    id: params.appointmentId,
    startAtIso: params.startAtIso?.trim() || undefined,
  };
}

/**
 * Monta MessagingPatient a partir de dados mínimos disponíveis nos fluxos legados.
 */
export function buildMessagingPatient(params: {
  id?: string | null;
  fullName?: string | null;
  cpf?: string | null;
  phone?: string | null;
  forReference?: string | null;
  patientActorReference?: string | null;
}): MessagingPatient | null {
  const cpfFromReference = extractCpfFromPatientReference(
    params.patientActorReference,
  );
  const cpf = normalizeCpf(
    String(params.cpf ?? cpfFromReference ?? "").trim(),
  );
  const forReference = String(params.forReference ?? "").trim() || undefined;
  const fullName = String(params.fullName ?? "Paciente").trim() || "Paciente";

  const id =
    String(params.id ?? "").trim() ||
    (cpf.length === 11 ? `patient-cpf-${cpf}` : "") ||
    (forReference ? `patient-ref-${forReference}` : "");

  if (!id) {
    console.warn(`${LOG_PREFIX} Paciente sem identificador para envio.`);
    return null;
  }

  if (!forReference && cpf.length !== 11) {
    console.warn(`${LOG_PREFIX} CPF inválido ou ausente para envio automático.`);
    return null;
  }

  return {
    id,
    fullName,
    cpf: cpf.length === 11 ? cpf : undefined,
    phone: String(params.phone ?? "").trim() || undefined,
    forReference,
  };
}

/**
 * Resolve requesterReference para FluxZap (PractitionerRole ou env de dev).
 */
export function resolveFluxzapRequesterReference(
  requesterReference?: string | null,
): string | null {
  const fromParams = String(requesterReference ?? "").trim();
  if (fromParams) return fromParams;

  const fromEnv = String(
    import.meta.env.VITE_FLUXZAP_REQUESTER_REFERENCE ?? "",
  ).trim();
  return fromEnv || null;
}

/**
 * Extrai nome usual/oficial de um recurso Patient FHIR.
 */
export function extractPatientDisplayName(
  patient?: fhir5.Patient | null,
  fallback = "Paciente",
): string {
  const nameEntry = patient?.name?.find(
    (entry) => entry.use === "usual" || entry.use === "official",
  );
  return nameEntry?.text?.trim() || fallback;
}

/**
 * Extrai telefone celular/fixo de um recurso Patient FHIR.
 */
export function extractPatientPhone(patient?: fhir5.Patient | null): string {
  const phoneEntry = patient?.telecom?.find(
    (entry) => entry.system === "phone",
  );
  return String(phoneEntry?.value ?? "").trim();
}

/**
 * Monta referência PractitionerRole no padrão usado pelo SendWhatsAppMessageDialog.
 */
export function buildPractitionerRoleRequesterReference(params: {
  organizationId: string;
  practitionerUserId: string;
}): string {
  const orgId = normalizeAutomationOrganizationId(params.organizationId);
  const practitionerUserId = String(params.practitionerUserId ?? "").trim();
  return `PractitionerRole/${orgId}-${practitionerUserId}`;
}

export function logAutomationTriggerSkip(reason: string): void {
  console.warn(`${LOG_PREFIX} ${reason}`);
}

export function logAutomationTriggerError(message: string, error?: unknown): void {
  console.error(`${LOG_PREFIX} ${message}`, error);
}
